<!--
  Copyright (C) Ascensio System SIA, 2009-2026

  This program is a free software product. You can redistribute it and/or
  modify it under the terms of the GNU Affero General Public License (AGPL)
  version 3 as published by the Free Software Foundation, together with the
  additional terms provided in the LICENSE file.

  This program is distributed WITHOUT ANY WARRANTY; without even the implied
  warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. For
  details, see the GNU AGPL at: https://www.gnu.org/licenses/agpl-3.0.html

  You can contact Ascensio System SIA by email at info@onlyoffice.com
  or by postal mail at 20A-6 Ernesta Birznieka-Upisha Street, Riga,
  LV-1050, Latvia, European Union.

  The interactive user interfaces in modified versions of the Program
  are required to display Appropriate Legal Notices in accordance with
  Section 5 of the GNU AGPL version 3.

  No trademark rights are granted under this License.

  All non-code elements of the Product, including illustrations,
  icon sets, and technical writing content, are licensed under the
  Creative Commons Attribution-ShareAlike 4.0 International License:
  https://creativecommons.org/licenses/by-sa/4.0/legalcode

  This license applies only to such non-code elements and does not
  modify or replace the licensing terms applicable to the Program's
  source code, which remains licensed under the GNU Affero General
  Public License v3.

  SPDX-License-Identifier: AGPL-3.0-only
-->
<template>
	<NcDialog
		class="onlyoffice-download-picker"
		:name="t('onlyoffice', 'Download as')"
		:buttons="buttons"
		@update:open="$emit('close', null)">
		<div ref="containerRef" class="onlyoffice-download-container" tabindex="-1">
			<p>{{ t('onlyoffice', 'Choose a format to convert {fileName}', { fileName }) }}</p>
			<NcSelect
				v-model="selectedFormat"
				inputId="onlyoffice-download-select"
				class="onlyoffice-download-select"
				:options="selectOptions"
				:reduce="(option) => option.id"
				:clearable="false"
				labelOutside
				:aria-label-combobox="t('onlyoffice', 'Format')" />
		</div>
	</NcDialog>
</template>

<script setup lang="ts">
import { t } from '@nextcloud/l10n'
import { generateUrl } from '@nextcloud/router'
import { computed, onMounted, ref } from 'vue'
import NcDialog from '@nextcloud/vue/components/NcDialog'
import NcSelect from '@nextcloud/vue/components/NcSelect'

declare const appName: string

const props = defineProps<{
	fileName: string
	fileId: number | string
	extension: string
	saveasFormats: string[]
}>()

const emit = defineEmits<{
	close: [value: boolean | null]
}>()

const selectedFormat = ref(props.extension)
const containerRef = ref<HTMLElement | null>(null)

onMounted(() => {
	containerRef.value?.focus()
})

const selectOptions = computed(() => [
	{ id: props.extension, label: t('onlyoffice', 'Original format') },
	...props.saveasFormats.map((ext) => ({ id: ext, label: ext })),
])

const buttons = computed(() => [
	{
		label: t('core', 'Cancel'),
		variant: 'secondary' as const,
		callback: () => emit('close', null),
	},
	{
		label: t('onlyoffice', 'Download'),
		variant: 'primary' as const,
		callback: () => {
			location.href = generateUrl('apps/' + appName + '/downloadas?fileId={fileId}&toExtension={toExtension}', {
				fileId: props.fileId,
				toExtension: selectedFormat.value,
			})
			emit('close', true)
		},
	},
])
</script>

<style scoped lang="scss">
.onlyoffice-download-picker :deep(.modal-container) {
	width: auto !important;
	min-width: min(558px, 90%);
	padding-block: 15px 25px !important;
	padding-inline: 25px !important;
}

.onlyoffice-download-picker :deep(.dialog__name) {
	margin-bottom: 0;
}

.onlyoffice-download-picker :deep(.modal-container__content) {
	row-gap: 26px;
}

.onlyoffice-download-picker :deep(.modal-container__close) {
	top: 15px !important;
	inset-inline-end: 15px !important;
}

.onlyoffice-download-picker :deep(.dialog__content) {
	padding-inline-end: 0;
}

.onlyoffice-download-picker :deep(.dialog__actions) {
	justify-content: flex-end;
	margin-block: 0 !important;
	padding-inline-end: 0;
	padding: 14px 0 0;
}

.onlyoffice-download-picker :deep(.dialog__actions .button-vue) {
	--button-size: 34px;
}

.onlyoffice-download-container {
	display: flex;
	flex-direction: column;
	row-gap: 12px;

	&:focus {
		outline: none;
	}
}

.onlyoffice-download-select {
	width: fit-content;
}

@media only screen and (max-width: 512px) {
	.onlyoffice-download-picker :deep(.modal-wrapper .modal-container) {
		height: unset;
		max-height: 90%;
		position: relative;
		top: unset;
		border-radius: var(--border-radius-element);
	}
}
</style>
